<template>
  <transition name="pi-fade">
    <div v-if="show" class="pi-overlay" @click.self="$emit('close')">
      <div class="pi-box" role="dialog" aria-modal="true" aria-labelledby="pi-title">

        <header class="pi-head">
          <div>
            <h2 id="pi-title">Partners oficiales</h2>
            <p>Son los que aparecen para elegir en el formulario, en “¿Fuiste referido por un partner?”. Si la persona no encuentra el suyo, elige “Otro” y lo escribe.</p>
          </div>
          <button class="pi-close" aria-label="Cerrar" @click="$emit('close')">×</button>
        </header>

        <!-- Alta -->
        <form class="pi-add" @submit.prevent="agregar">
          <input
            v-model="nuevo"
            type="text"
            maxlength="100"
            placeholder="Nombre del partner (ej. EADA Business School)"
            aria-label="Nombre del partner nuevo"
            :disabled="guardando"
          />
          <button type="submit" class="pi-btn" :disabled="!nuevo.trim() || guardando">Agregar</button>
        </form>
        <p v-if="error" class="pi-error">{{ error }}</p>

        <!-- Lista -->
        <div class="pi-body">
          <p v-if="cargando" class="pi-empty">Cargando…</p>

          <p v-else-if="!partners.length" class="pi-empty">
            Todavía no hay partners cargados. Mientras la lista esté vacía, el formulario muestra solo el campo para escribir el nombre.
          </p>

          <ul v-else class="pi-list">
            <li v-for="(p, i) in partners" :key="p.id" class="pi-item" :class="{ 'is-off': !p.activo }">
              <div class="pi-order">
                <button class="pi-icon" :disabled="i === 0 || guardando" aria-label="Subir" title="Subir" @click="mover(i, -1)">▲</button>
                <button class="pi-icon" :disabled="i === partners.length - 1 || guardando" aria-label="Bajar" title="Bajar" @click="mover(i, 1)">▼</button>
              </div>

              <template v-if="editando === p.id">
                <input
                  ref="editInput"
                  v-model="nombreEdicion"
                  class="pi-edit"
                  type="text"
                  maxlength="100"
                  aria-label="Nuevo nombre"
                  @keyup.enter="guardarNombre(p)"
                  @keyup.esc="editando = null"
                />
                <button class="pi-btn pi-btn--sm" :disabled="guardando || !nombreEdicion.trim()" @click="guardarNombre(p)">Guardar</button>
                <button class="pi-btn pi-btn--ghost pi-btn--sm" @click="editando = null">Cancelar</button>
              </template>

              <template v-else>
                <span class="pi-name">{{ p.nombre }}</span>
                <span v-if="!p.activo" class="pi-tag">Oculto</span>

                <label class="pi-switch" :title="p.activo ? 'Visible en el formulario' : 'Oculto en el formulario'">
                  <input type="checkbox" :checked="p.activo" :disabled="guardando" @change="cambiarActivo(p, $event.target.checked)" />
                  <span>{{ p.activo ? 'Visible' : 'Oculto' }}</span>
                </label>
                <button class="pi-btn pi-btn--ghost pi-btn--sm" :disabled="guardando" @click="editar(p)">Renombrar</button>
                <button class="pi-btn pi-btn--danger pi-btn--sm" :disabled="guardando" @click="eliminar(p)">Eliminar</button>
              </template>
            </li>
          </ul>
        </div>

        <footer class="pi-foot">
          Los intakes guardan el nombre que eligió la persona: renombrar o eliminar un partner no cambia los intakes que ya lo tienen.
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'

/**
 * Administración de los partners oficiales del formulario de intake.
 * API: /admin/partners (solo admins). El formulario lee /partners (los activos).
 */
export default {
  name: 'PartnersIntake',
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      partners: [],
      cargando: false,
      guardando: false,
      error: '',
      nuevo: '',
      editando: null,
      nombreEdicion: ''
    }
  },
  computed: {
    base() {
      return `${process.env.VUE_APP_API_URL}admin/partners`
    }
  },
  watch: {
    show(abierto) {
      if (abierto) this.cargar()
    }
  },
  methods: {
    mensajeDe(e, porDefecto) {
      const data = e && e.response && e.response.data
      if (data && data.message) return data.message
      if (e && e.response && e.response.status === 403) return 'No tienes permiso para administrar partners.'
      return porDefecto
    },

    async cargar() {
      this.cargando = true
      this.error = ''
      try {
        const { data } = await axios.get(this.base)
        this.partners = data.partners || []
      } catch (e) {
        this.error = this.mensajeDe(e, 'No se pudieron cargar los partners.')
      } finally {
        this.cargando = false
      }
    },

    async agregar() {
      const nombre = this.nuevo.trim()
      if (!nombre) return
      this.guardando = true
      this.error = ''
      try {
        const { data } = await axios.post(this.base, { nombre })
        this.partners.push(data.partner)
        this.nuevo = ''
      } catch (e) {
        this.error = this.mensajeDe(e, 'No se pudo agregar el partner.')
      } finally {
        this.guardando = false
      }
    },

    editar(p) {
      this.editando = p.id
      this.nombreEdicion = p.nombre
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.editInput) ? this.$refs.editInput[0] : this.$refs.editInput
        if (el) el.focus()
      })
    },

    async guardarNombre(p) {
      const nombre = this.nombreEdicion.trim()
      if (!nombre || nombre === p.nombre) {
        this.editando = null
        return
      }
      await this.actualizar(p, { nombre })
      if (!this.error) this.editando = null
    },

    cambiarActivo(p, activo) {
      return this.actualizar(p, { activo })
    },

    async actualizar(p, cambios) {
      this.guardando = true
      this.error = ''
      try {
        const { data } = await axios.put(`${this.base}/${p.id}`, cambios)
        Object.assign(p, data.partner)
      } catch (e) {
        this.error = this.mensajeDe(e, 'No se pudo guardar el cambio.')
      } finally {
        this.guardando = false
      }
    },

    /** Intercambia el lugar con el vecino y guarda el orden de los dos. */
    async mover(i, delta) {
      const j = i + delta
      if (j < 0 || j >= this.partners.length) return

      const lista = this.partners.slice()
      const tmp = lista[i]
      lista[i] = lista[j]
      lista[j] = tmp
      this.partners = lista

      this.guardando = true
      this.error = ''
      try {
        // Se renumera toda la lista: así no quedan empates de orden.
        await Promise.all(lista.map((p, idx) =>
          p.orden === idx + 1 ? null : axios.put(`${this.base}/${p.id}`, { orden: idx + 1 }).then(() => { p.orden = idx + 1 })
        ))
      } catch (e) {
        this.error = this.mensajeDe(e, 'No se pudo guardar el orden.')
        await this.cargar()
      } finally {
        this.guardando = false
      }
    },

    async eliminar(p) {
      if (!window.confirm(`¿Eliminar "${p.nombre}"? Deja de aparecer en el formulario. Los intakes que ya lo eligieron no cambian.`)) return
      this.guardando = true
      this.error = ''
      try {
        await axios.delete(`${this.base}/${p.id}`)
        this.partners = this.partners.filter((x) => x.id !== p.id)
      } catch (e) {
        this.error = this.mensajeDe(e, 'No se pudo eliminar el partner.')
      } finally {
        this.guardando = false
      }
    }
  }
}
</script>

<style scoped>
.pi-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(10, 10, 11, 0.5);
}

.pi-box {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 620px;
  max-height: 88vh;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(10, 10, 11, 0.25);
  overflow: hidden;
  font-family: 'Geist', 'Instrument Sans', sans-serif;
  color: #0a0a0b;
}

.pi-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px 12px;
}

.pi-head h2 { margin: 0; font-size: 19px; font-weight: 600; }
.pi-head p { margin: 6px 0 0; font-size: 13px; line-height: 1.5; color: #6e6e76; }

.pi-close {
  border: 0;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #6e6e76;
  cursor: pointer;
}

.pi-close:hover { color: #0a0a0b; }

.pi-add {
  display: flex;
  gap: 8px;
  padding: 0 22px 12px;
}

.pi-add input,
.pi-edit {
  flex: 1 1 auto;
  min-width: 0;
  padding: 9px 12px;
  border: 1px solid #eceae4;
  border-radius: 10px;
  font-size: 14px;
}

.pi-add input:focus,
.pi-edit:focus {
  outline: none;
  border-color: #fc7010;
  box-shadow: 0 0 0 3px rgba(252, 112, 16, 0.14);
}

.pi-error {
  margin: 0 22px 10px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fdecf5;
  color: #b91e7c;
  font-size: 13px;
}

.pi-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 22px 10px;
  border-top: 1px solid #eceae4;
}

.pi-empty {
  padding: 26px 6px;
  text-align: center;
  font-size: 13px;
  color: #6e6e76;
}

.pi-list { list-style: none; margin: 0; padding: 0; }

.pi-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 0;
  border-bottom: 1px solid #f1efea;
}

.pi-item.is-off .pi-name { color: #a0a0a8; }

.pi-order { display: flex; flex-direction: column; }

.pi-icon {
  width: 22px;
  height: 16px;
  border: 0;
  background: transparent;
  font-size: 9px;
  color: #6e6e76;
  cursor: pointer;
}

.pi-icon:disabled { opacity: 0.25; cursor: default; }

.pi-name { flex: 1 1 160px; min-width: 0; font-size: 14px; font-weight: 500; overflow-wrap: anywhere; }

.pi-tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: #f4f3f0;
  font-size: 11px;
  font-weight: 600;
  color: #6e6e76;
}

.pi-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 12px;
  color: #383840;
  cursor: pointer;
}

.pi-btn {
  border: 1px solid #fc7010;
  background: #fc7010;
  color: #fff;
  border-radius: 10px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.pi-btn:disabled { opacity: 0.5; cursor: default; }
.pi-btn--sm { padding: 6px 11px; font-size: 12px; }
.pi-btn--ghost { background: #fff; color: #383840; border-color: #eceae4; }
.pi-btn--ghost:hover:not(:disabled) { border-color: #0a0a0b; }
.pi-btn--danger { background: #fff; color: #b91e7c; border-color: #f5bdd9; }
.pi-btn--danger:hover:not(:disabled) { background: #b91e7c; color: #fff; }

.pi-foot {
  padding: 12px 22px 16px;
  border-top: 1px solid #eceae4;
  background: #fafaf9;
  font-size: 12px;
  color: #6e6e76;
}

.pi-fade-enter-active,
.pi-fade-leave-active { transition: opacity 0.15s ease; }
.pi-fade-enter,
.pi-fade-leave-to { opacity: 0; }

@media (max-width: 520px) {
  .pi-add { flex-direction: column; }
}
</style>
